import { METRIC_META, WEIGHT_GAIN_DAY_LBS } from '@heartcoach/care';
import type { MetricKind, PatientView } from '../../lib/types';
import { BP_CLASS, classifyBp, fmtAgo, fmtBp } from '../../lib/format';
import { Icon } from '../../components/HeartMark';
import { Card } from '../../components/Bits';
import { BpChart } from '../../components/BpChart';

const LOOK: Record<MetricKind, { icon: Parameters<typeof Icon>[0]['name']; tone: string }> = {
  'heart-rate': { icon: 'health', tone: 'red' },
  hrv: { icon: 'trend', tone: 'green' },
  'sleep-hours': { icon: 'star', tone: 'purple' },
  'weight-lbs': { icon: 'profile', tone: 'blue' },
  'exertion-minutes': { icon: 'steps', tone: 'gold' },
};

/** Health: the numbers this patient's track asks for, then the trend chart and the readings behind it. */
export function HealthTab({ view }: { view: PatientView }) {
  const m = view.metrics;
  // What this patient is asked to watch, in their track's order: weight first for heart failure,
  // pulse first for fibrillation. A tile nobody is asked for is a tile nobody fills in.
  const tiles = view.track.metrics.map((kind: MetricKind) => {
    const meta = METRIC_META[kind];
    const sample = m[kind];
    return {
      kind,
      icon: LOOK[kind].icon,
      tone: LOOK[kind].tone,
      label: meta.label,
      unit: meta.unit,
      value: sample ? sample.value.toFixed(meta.decimals) : '—',
    };
  });
  const readings = view.trend.filter((t) => t.reading !== null).slice().reverse();

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Health Metrics</h1>
          <span className="hint">Your last seven days</span>
        </div>
        <a className="button primary" href="#/bp/log">
          <Icon name="plus" size={16} /> Log a reading
        </a>
      </div>

      <div className="metric-row">
        {tiles.map((t) => (
          <div className="tile" key={t.kind}>
            <span className={`bubble bubble-${t.tone} bubble-sm tile-icon`}>
              <Icon name={t.icon} size={17} />
            </span>
            <strong className={`tile-value tone-${t.tone}`}>
              {t.value} <small>{t.unit}</small>
            </strong>
            <span className="tile-label">{t.label}</span>
          </div>
        ))}
      </div>

      <div className="grid-2">
        <Card>
          <div className="card-head">
            <div>
              <h2>Blood Pressure</h2>
              <span className="hint">7-day trend</span>
            </div>
            <span className="pill pill-red">
              <strong>{fmtBp(view.latestReading)}</strong>
              {view.systolicChange !== null ? (
                <span className={view.systolicChange <= 0 ? 'good' : 'warn'}>
                  {view.systolicChange <= 0 ? '↓' : '↑'} {Math.abs(view.systolicChange)} pts
                </span>
              ) : null}
            </span>
          </div>
          <BpChart trend={view.trend} />
          <div className="legend">
            <span>
              <i className="swatch" /> Systolic
            </span>
            <span>
              <i className="swatch diastolic" /> Diastolic
            </span>
          </div>
        </Card>

        <div className="stack">
          <Card>
            <h2>Recent readings</h2>
            {readings.length === 0 ? <p className="hint">No readings logged in the last week.</p> : null}
            <ul className="reading-list">
              {readings.map((t, i) => {
                const r = t.reading!;
                const cls = BP_CLASS[classifyBp(r.systolic, r.diastolic)];
                return (
                  <li key={i}>
                    <span className="row-main">
                      <strong>
                        {r.systolic}/{r.diastolic}
                      </strong>
                      <span className="hint">
                        {t.label} · {fmtAgo(r.at, view.now)}
                      </span>
                    </span>
                    <span className={`badge ${cls?.tone === 'good' ? 'badge-green' : cls?.tone === 'bad' ? 'badge-red' : 'badge-gold'}`}>{cls?.label}</span>
                  </li>
                );
              })}
            </ul>
          </Card>
          <Card>
            <h2>Connected devices</h2>
            <p className="hint">A cuff, a scale or a watch can fill in some of these for you. The readings you log yourself are what earn HeartCoins.</p>
            <a className="button secondary small" href="#/devices">
              Connect a device
            </a>
          </Card>
        </div>
      </div>
    </>
  );
}
